import React, { useMemo, useState } from "react";
import "../../../css/calculators/MortgageBorrow.css";
import SecondaryButton from "../../designSystem/FilledButton";
import PrimaryButton from "../../designSystem/OutlinedButton";
import CustomSelect from "../../designSystem/CustomSelect";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";

const initialState = {
  premium: "",
  incomeTaxRate: "",
  employeeNiRate: "",
  employerNiRate: "13.8",
  corporationTaxRate: "",
};

const incomeTaxOptions = [
  { value: "", label: "Please select" },
  { value: "19", label: "19%" },
  { value: "20", label: "20%" },
  { value: "21", label: "21%" },
  { value: "40", label: "40%" },
  { value: "41", label: "41%" },
  { value: "45", label: "45%" },
  { value: "46", label: "46%" },
];

const employeeNiOptions = [
  { value: "", label: "Please select" },
  { value: "2", label: "2.00%" },
  { value: "10", label: "10.00%" },
];

const corporationTaxOptions = [
  { value: "", label: "Please select" },
  { value: "18", label: "18%" },
  { value: "19", label: "19%" },
  { value: "20", label: "20%" },
  { value: "25", label: "25%" },
];

const formatCurrency = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export default function RelevantLifeTaxCalculator() {
  const [formData, setFormData] = useState(initialState);
  const [hasCalculated, setHasCalculated] = useState(false);

const handleChange = (e) => {
  const { name, value } = e.target;

  if (name === "employerNiRate") {
    const cleanedValue = value.replace(/[^\d.]/g, "");
    setFormData((prev) => ({
      ...prev,
      [name]: cleanedValue,
    }));
    return;
  }

  setFormData((prev) => ({
    ...prev,
    [name]: value,
  }));
};

  const handleClear = () => {
    setFormData(initialState);
    setHasCalculated(false);
  };

  const handleCalculate = (e) => {
    e.preventDefault();
    setHasCalculated(true);
  };

  const results = useMemo(() => {
    const premium = Number(formData.premium || 0);
    const incomeTaxRate = Number(formData.incomeTaxRate || 0) / 100;
    const employeeNiRate = Number(formData.employeeNiRate || 0) / 100;
    const employerNiRate = Number(formData.employerNiRate || 0) / 100;
    const corporationTaxRate = Number(formData.corporationTaxRate || 0) / 100;

    if (
      !premium ||
      !formData.incomeTaxRate ||
      !formData.employeeNiRate ||
      !formData.employerNiRate ||
      !formData.corporationTaxRate
    ) {
      return null;
    }

    const effectiveEmployeeNiRate = 0.0325;
    const netRetentionRate = 1 - incomeTaxRate - effectiveEmployeeNiRate;

    if (netRetentionRate <= 0) return null;

    // Gross amount needed so that employee is left with the premium after tax + NI
    const grossRequired = premium / netRetentionRate;

    const personalIncomeTax = grossRequired * incomeTaxRate;
    const personalEmployeeNi = grossRequired * effectiveEmployeeNiRate;
    const personalEmployerNi = grossRequired * employerNiRate;

    // Match the layout in the screenshot:
    // premium shown as "Employee Pays", then tax/NI added on top
    const personalSubTotal =
      premium + personalIncomeTax + personalEmployeeNi + personalEmployerNi;

    const personalCorporationTaxRelief = personalSubTotal * corporationTaxRate;
    const personalTotal = personalSubTotal - personalCorporationTaxRelief;

    const relevantLifeSubTotal = premium;
    const relevantLifeCorporationTaxRelief =
      relevantLifeSubTotal * corporationTaxRate;
    const relevantLifeTotal =
      relevantLifeSubTotal - relevantLifeCorporationTaxRelief;

    const employerSaving = personalTotal - relevantLifeTotal;
    const savingPercentage =
      personalTotal > 0 ? (employerSaving / personalTotal) * 100 : 0;

    return {
      grossRequired,
      personalIncomeTax,
      personalEmployeeNi,
      personalEmployerNi,
      personalSubTotal,
      personalCorporationTaxRelief,
      personalTotal,
      relevantLifeSubTotal,
      relevantLifeCorporationTaxRelief,
      relevantLifeTotal,
      employerSaving,
      savingPercentage,
    };
  }, [formData]);

  return (
    <div className="borrow-calculator-page">
      <div className="borrow-form-wrap">
        <div className="title burgandy">Relevant Life Tax Calculator</div>

        <form onSubmit={handleCalculate}>
          <div className="borrow-field-group">
            <div className="borrow-label content burgandy align-left">Premium (£)</div>
            <div className="select-wrapper">
              <FormattedNumberInput
                name="premium"
                value={formData.premium}
                onChange={(raw) =>
                  setFormData((prev) => ({
                    ...prev,
                    premium: raw,
                  }))
                }
                className="borrow-input content"
              />
            </div>
          </div>

          <div className="borrow-field-group">
            <div className="borrow-label content burgandy align-left">
              Highest applicable income tax rate (%)
            </div>
            <div className="select-wrapper">
              {/* <select
                name="incomeTaxRate"
                value={formData.incomeTaxRate}
                onChange={handleChange}
                className="borrow-input"
              >
                {incomeTaxOptions.map((option) => (
                  <option key={option.value || "income"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select> */}
              <CustomSelect
                name="incomeTaxRate"
                options={incomeTaxOptions}
                value={formData.incomeTaxRate}
                onChange={handleChange}
                placeholder="Please select income tax rate"
              />
            </div>
          </div>

          <div className="borrow-field-group">
            <div className="borrow-label content burgandy align-left">
              Employee national insurance (NI) rate (%)
            </div>
            <div className="select-wrapper">
              {/* <select
                name="employeeNiRate"
                value={formData.employeeNiRate}
                onChange={handleChange}
                className="borrow-input"
              >
                {employeeNiOptions.map((option) => (
                  <option
                    key={option.value || "employeeNi"}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select> */}
              <CustomSelect
                name="employeeNiRate"
                options={employeeNiOptions}
                value={formData.employeeNiRate}
                onChange={handleChange}
                placeholder="Please select employee NI rate"
              />
            </div>
          </div>

          <div className="borrow-field-group">
            <div className="borrow-label content burgandy align-left">
              Employer national insurance (NI) rate (%)
            </div>
            <input
              type="text"
              name="employerNiRate"
              value={formData.employerNiRate}
              onChange={handleChange}
              className="borrow-input content"
              placeholder="13.8"
            />
          </div>

          <div className="borrow-field-group">
            <div className="borrow-label content burgandy align-left">Company corp. tax rate (%)</div>
            <div className="select-wrapper">
              {/* <select
                name="corporationTaxRate"
                value={formData.corporationTaxRate}
                onChange={handleChange}
                className="borrow-input"
              >
                {corporationTaxOptions.map((option) => (
                  <option key={option.value || "corp"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select> */}
              <CustomSelect
                name="corporationTaxRate"
                options={corporationTaxOptions}
                value={formData.corporationTaxRate}
                onChange={handleChange}
                placeholder="Please select corporation tax rate"
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 16,
              marginTop: 24,
              justifyContent: "flex-end",
            }}
          >
            <SecondaryButton onClick={handleClear} text="Clear" />
            <PrimaryButton onClick={handleCalculate} text="Calculate" />
          </div>
        </form>
      </div>

      {hasCalculated && results && (
        <div className="borrow-result-wrap">
          <div className="result-container">
            <div className="title ">Result</div>

            <div className="stamp-duty-breakdown-table-wrap">
              <table className="stamp-duty-breakdown-table">
                <thead>
                  <tr>
                    <th>Your Details</th>
                    <th>Personal Life Policy</th>
                    <th>Relevant Life Policy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Employee Pays</td>
                    <td>{formatCurrency(Number(formData.premium || 0))}</td>
                    <td>n/a</td>
                  </tr>
                  <tr>
                    <td>Employer Pays</td>
                    <td>n/a</td>
                    <td>{formatCurrency(Number(formData.premium || 0))}</td>
                  </tr>
                  <tr>
                    <td>Employee NI</td>
                    <td>{formatCurrency(results.personalEmployeeNi)}</td>
                    <td>n/a</td>
                  </tr>
                  <tr>
                    <td>Employee Income Tax</td>
                    <td>{formatCurrency(results.personalIncomeTax)}</td>
                    <td>n/a</td>
                  </tr>
                  <tr>
                    <td>Employer NI</td>
                    <td>{formatCurrency(results.personalEmployerNi)}</td>
                    <td>n/a</td>
                  </tr>
                  <tr>
                    <td>Sub-Total Cost</td>
                    <td>{formatCurrency(results.personalSubTotal)}</td>
                    <td>{formatCurrency(results.relevantLifeSubTotal)}</td>
                  </tr>
                  <tr>
                    <td>Less Corporation Tax</td>
                    <td>
                      {formatCurrency(results.personalCorporationTaxRelief)}
                    </td>
                    <td>
                      {formatCurrency(results.relevantLifeCorporationTaxRelief)}
                    </td>
                  </tr>
                  <tr>
                    <td>Total</td>
                    <td>{formatCurrency(results.personalTotal)}</td>
                    <td>{formatCurrency(results.relevantLifeTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="stamp-duty-summary-copy">
              <div className="content">
                Saving Via Relevant Life Policy Will Net You
              </div>
              <div className="subtitle-semibold">
                {formatCurrency(results.employerSaving)} or{" "}
                {Math.round(results.savingPercentage)}%
              </div>
            </div>
            <div className="borrow-note-section">
              <div className="title ">PLEASE NOTE:</div>
              <p className="help-text">
                This is a generic calculation that does not take into account
                the individuals circumstances, it should not therefore be relied
                on.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
